import { QuiverLogo } from '../../../components_v1/icons/QuiverLogo'

interface Props {
  className?: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** Override the dimmed "OS" color — for themed chrome (default: text-gray-400). */
  osClassName?: string
  /** Override the feather color (default: text-indigo-600 dark:text-indigo-500). */
  featherClassName?: string
}

/**
 * The QuiverOS wordmark — the ONE logo. Use this everywhere a logo appears
 * (marketing nav, auth, Desk chrome) so the mark stays identical across surfaces.
 *
 * The design is shared (lowercase `quiver` + the quiver feather as the [i] + a
 * dimmed `OS` suffix, all `font-normal`); only the type scale, feather size, and
 * the per-size optical margins differ. Themed surfaces (the Desk) pass
 * `className` / `osClassName` / `featherClassName` to map onto their own tokens.
 */
// `detail` = opacity of the binding ticks on the quiver body; faded at the small
// sizes (where they muddy), full at md/lg. Dial per size to taste.
const SIZES = {
  xs: {
    text: 'text-[13px]',
    w: '10',
    h: '15',
    stroke: '#FFF',
    feather: 'ml-[-1.85px] mr-[-4.85px] mb-[-1.25px]',
    detail: 0.25,
  },
  sm: {
    text: 'text-2xl',
    w: '18',
    h: '28',
    stroke: '#FFF',
    feather: 'ml-[-3.5px] mr-[-9px] mb-[-1px]',
    detail: 0.55,
  },
  md: {
    text: 'text-4xl',
    w: '26',
    h: '39',
    stroke: '#FFF',
    feather: 'ml-[-4.5px] mr-[-12.75px] mb-[-1px]',
    detail: 1,
  },
  lg: {
    text: 'text-6xl',
    w: '48',
    h: '60',
    stroke: '#FFF',
    feather: 'ml-[-9px] mr-[-23.5px] mb-[1px]',
    detail: 1,
  },
} as const

export function AppLogo({
  className = '',
  size = 'md',
  osClassName = 'text-gray-400',
  featherClassName = 'text-indigo-600 dark:text-indigo-500',
}: Props) {
  const s = SIZES[size]
  return (
    <h1
      className={`flex items-center ${s.text} font-normal leading-none tracking-wide text-gray-900 dark:text-gray-50 ${className}`}
    >
      qu
      <span className={`relative -top-px ${featherClassName} ${s.feather}`}>
        <QuiverLogo
          width={s.w}
          height={s.h}
          stroke={s.stroke}
          weight="normal"
          detail={s.detail}
        />
      </span>
      ver
      <span className={`font-normal tracking-tighter ${osClassName}`}>OS</span>
    </h1>
  )
}
